import React, { useState } from 'react';
import PropTypes from 'prop-types';
import BarChart from './BarChart';
import './FeatureUsageChart.css';

/**
 * FeatureUsageChart - Horizontal bar chart for top 10 most-used features
 * 
 * Displays feature usage statistics with:
 * - Feature name, usage count, and percentage
 * - Click handler for detailed statistics
 * - Responsive design for all device sizes
 * - Full accessibility support
 * 
 * @param {Object} props
 * @param {Array} props.data - Feature usage data: [{ featureId, name, count, percentage }]
 * @param {boolean} props.loading - Loading state
 * @param {string} props.error - Error message
 * @param {Function} props.onFeatureClick - Callback when feature is clicked (featureId, featureName)
 */
const FeatureUsageChart = ({ data = [], loading = false, error = null, onFeatureClick = null }) => {
  const [selectedFeature, setSelectedFeature] = useState(null);

  // Handle feature click
  const handleFeatureClick = (featureId, featureName) => {
    setSelectedFeature(featureId);
    if (onFeatureClick) {
      onFeatureClick(featureId, featureName);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (event, featureId, featureName) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleFeatureClick(featureId, featureName);
    }
  };

  // Prepare chart data for BarChart component
  const chartData = {
    labels: data.map(item => item.name || item.featureName),
    datasets: [{
      label: 'Usage Count',
      data: data.map(item => item.count),
      backgroundColor: data.map((_, index) => 
        index === 0 ? 'rgba(16, 185, 129, 0.8)' : // Top feature - green
        index === 1 ? 'rgba(59, 130, 246, 0.8)' : // Second - blue
        index === 2 ? 'rgba(139, 92, 246, 0.8)' : // Third - purple
        'rgba(107, 114, 128, 0.8)' // Others - gray
      ),
      borderColor: data.map((_, index) => 
        index === 0 ? 'rgba(16, 185, 129, 1)' :
        index === 1 ? 'rgba(59, 130, 246, 1)' :
        index === 2 ? 'rgba(139, 92, 246, 1)' :
        'rgba(107, 114, 128, 1)'
      ),
      borderWidth: 2
    }]
  };

  // Chart options for horizontal bar chart
  const chartOptions = {
    indexAxis: 'y', // Horizontal bars
    responsive: true,
    maintainAspectRatio: false,
    onClick: (event, elements) => {
      if (elements.length > 0) {
        const index = elements[0].index;
        const feature = data[index];
        if (feature) {
          handleFeatureClick(feature.featureId, feature.name || feature.featureName);
        }
      }
    },
    plugins: {
      legend: {
        display: false // Hide legend for cleaner look
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const index = context.dataIndex;
            const feature = data[index];
            return [
              `Usage: ${feature.count.toLocaleString()}`,
              `Percentage: ${feature.percentage.toFixed(1)}%`
            ];
          }
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          callback: (value) => value.toLocaleString()
        },
        title: {
          display: true,
          text: 'Usage Count'
        }
      },
      y: {
        ticks: {
          autoSkip: false,
          font: {
            size: 12
          }
        }
      }
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="feature-usage-chart">
        <div className="feature-usage-chart__header">
          <h3 className="feature-usage-chart__title">Top 10 Features</h3>
        </div>
        <div className="feature-usage-chart__loading">
          <div className="feature-usage-chart__spinner" role="status" aria-live="polite">
            <span className="sr-only">Loading feature usage data...</span>
          </div>
          <p>Loading feature usage data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="feature-usage-chart">
        <div className="feature-usage-chart__header">
          <h3 className="feature-usage-chart__title">Top 10 Features</h3>
        </div>
        <div className="feature-usage-chart__error" role="alert">
          <p className="feature-usage-chart__error-message">{error}</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div className="feature-usage-chart">
        <div className="feature-usage-chart__header">
          <h3 className="feature-usage-chart__title">Top 10 Features</h3>
        </div>
        <div className="feature-usage-chart__empty">
          <p>No feature usage data available for the selected period.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="feature-usage-chart">
      <div className="feature-usage-chart__header">
        <h3 className="feature-usage-chart__title">Top 10 Features</h3>
        <p className="feature-usage-chart__subtitle">
          Most frequently used features (click for details)
        </p>
      </div>

      {/* Chart visualization */}
      <div 
        className="feature-usage-chart__chart-container"
        role="img"
        aria-label={`Bar chart showing top ${data.length} most-used features. ${data[0]?.name || data[0]?.featureName} is the most used with ${data[0]?.count} uses.`}
      >
        <BarChart 
          data={chartData}
          options={chartOptions}
          height={Math.max(300, data.length * 40)} // Dynamic height based on data
        />
      </div>

      {/* Data table for accessibility and detail view */}
      <div className="feature-usage-chart__table-container">
        <table className="feature-usage-chart__table">
          <caption className="sr-only">
            Feature usage statistics showing feature name, usage count, and percentage
          </caption>
          <thead>
            <tr>
              <th scope="col">Rank</th>
              <th scope="col">Feature</th>
              <th scope="col">Usage Count</th>
              <th scope="col">Percentage</th>
            </tr>
          </thead>
          <tbody>
            {data.map((feature, index) => (
              <tr 
                key={feature.featureId}
                className={`feature-usage-chart__row ${selectedFeature === feature.featureId ? 'feature-usage-chart__row--selected' : ''}`}
                onClick={() => handleFeatureClick(feature.featureId, feature.name || feature.featureName)}
                onKeyDown={(e) => handleKeyDown(e, feature.featureId, feature.name || feature.featureName)}
                tabIndex={0}
                role="button"
                aria-label={`View details for ${feature.name || feature.featureName}, used ${feature.count} times (${feature.percentage.toFixed(1)}%)`}
              >
                <td className="feature-usage-chart__rank">
                  {index === 0 && <span className="feature-usage-chart__medal">🥇</span>}
                  {index === 1 && <span className="feature-usage-chart__medal">🥈</span>}
                  {index === 2 && <span className="feature-usage-chart__medal">🥉</span>}
                  {index > 2 && <span className="feature-usage-chart__rank-number">{index + 1}</span>}
                </td>
                <td className="feature-usage-chart__feature-name">
                  {feature.name || feature.featureName}
                </td>
                <td className="feature-usage-chart__count">
                  {feature.count.toLocaleString()}
                </td>
                <td className="feature-usage-chart__percentage">
                  <div className="feature-usage-chart__percentage-bar">
                    <div 
                      className="feature-usage-chart__percentage-fill"
                      style={{ width: `${feature.percentage}%` }}
                      aria-hidden="true"
                    />
                    <span className="feature-usage-chart__percentage-text">
                      {feature.percentage.toFixed(1)}%
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Hint for interaction */}
      {onFeatureClick && (
        <p className="feature-usage-chart__hint">
          Click on any feature to view detailed statistics
        </p>
      )}
    </div>
  );
};

FeatureUsageChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    featureId: PropTypes.string.isRequired,
    name: PropTypes.string,
    featureName: PropTypes.string,
    count: PropTypes.number.isRequired,
    percentage: PropTypes.number.isRequired
  })),
  loading: PropTypes.bool,
  error: PropTypes.string,
  onFeatureClick: PropTypes.func
};

export default FeatureUsageChart;
